import {
  Arg,
  COMPONENT_STATE_ARGS,
  INPUT_FORM_ARGS,
  Metadata,
  pick,
} from '../../../utils/spds/metadata';
import { fn } from '@ember/helper';
import { isEmpty, or } from 'ember-truth-helpers';
import { reads } from 'macro-decorators';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import SearchInput from './search.gjs';
import Super from '../core/base.gjs';

const METADATA = Metadata.define({
  description: `Allows user to search for addresses with autocomplete suggestions.

### A note on usage
While the component can be used with any custom address suggestion provider or place search that return properly formatted data, it is recommended to use this component with the \`Shared::AddressAutocompleteWrapper\` component by providing the \`@useDSInput={{true}}\` argument in the EHR.

### Address Autocomplete
The address autocomplete component extends the search input component to provide address-specific functionality. It provides an interface for users to search for addresses and select from suggestions.

### Features
- Search for addresses as you type
- Display address suggestions from a provider
- Select an address from the suggestions
- Use as an input field to display the full selected address
- Custom callbacks for address selection and search events

### Address Suggestions Provider
The component requires an address suggestions provider function that accepts a query string and returns a Promise or value that resolves to an array of address objects. Each address object must have:
- text: The full address text to display in the suggestion list and used as the value for selection

The provider function should handle filtering based on the query and return relevant suggestions. If no suggestions are found, it should return an empty array.

### Address Search Places
The component requires an address search places function that returns detailed place information for a selected address. This function should return an array of place objects with at least a 'street' property. If the function returns an empty array or undefined value, the component will fall back to extracting the street name from the first part of the address text.

### Actions/Callbacks
- onAddressSelect: Called when an address is selected from the suggestions with the place object and full address text
- onSearched: Called when the search query changes, receives the current query text`,
  extends: [
    pick(COMPONENT_STATE_ARGS, ['disabled']),
    pick(INPUT_FORM_ARGS, [
      { name: 'helperText', initialValue: 'Helper text' },
      { name: 'label', initialValue: 'Address' },
      { name: 'placeholder', initialValue: '' },
    ]),
  ],
  meta: {
    addressSearchPlaces: Arg.action({
      description: 'Resolve address text to place object(s)',
    }).required(),
    addressSuggestionsProvider: Arg.action({
      description: 'Return suggestions for a query string',
    }).required(),
    onAddressSelect: Arg.action({ description: 'Called when an address is selected' }).required(),
    onSearched: Arg.action({ description: 'Called when a search is performed' }),
    selectedAddress: Arg.string({ description: 'Currently selected address display value' }),
  },
  name: 'Address Autocomplete',
});

export default class SpdsInputAddressAutocomplete extends Super {
  static metadata = this.setMetadata(METADATA, import.meta.url);

  baseClass = 'spds-input-address-autocomplete';

  @reads('searchInputComponent.trigger') searchInputTrigger;

  @service store;

  @tracked query = null;
  @tracked searchValue = null;
  @tracked addressSuggestions = [];

  setup = () => {
    let { selectedAddress } = this.args;
    this.searchValue = selectedAddress;
    this.query = selectedAddress;

    if (selectedAddress) {
      this.fetchAddressSuggestions(selectedAddress);
    }
  };

  fetchAddressSuggestions = async query => {
    this.args.onSearched?.(query);

    if (!this.args.addressSuggestionsProvider) {
      this.addressSuggestions = [];
      return [];
    }

    let suggestions = await Promise.resolve(this.args.addressSuggestionsProvider(query));
    let results = Array.isArray(suggestions) ? suggestions : [];

    this.addressSuggestions = results;

    return results;
  };

  handleSelect = async ({ target: menuItem }) => {
    if (!menuItem) return;

    let fullAddressText = menuItem.dataset.value;
    let places = await Promise.resolve(this.args.addressSearchPlaces(fullAddressText));
    let place = places?.length ? places[0] : { street: fullAddressText.split(',')[0].trim() };

    this.args.onAddressSelect(place, fullAddressText);
  };

  handleInput = ({ target: { value } }) => {
    this.searchValue = value;
    this.query = value;

    this.fetchAddressSuggestions(value);
  };

  <template>
    <SearchInput
      @disabled={{@disabled}}
      @disableAutofilter={{true}}
      @helperText={{@helperText}}
      @id={{this.id}}
      @label={{@label}}
      @menuOpenThreshold={{1}}
      @onChange={{this.handleSelect}}
      @onInput={{this.handleInput}}
      @placeholder={{or @placeholder "Search address"}}
      @registerComponent={{fn this.registerComponent "searchInputComponent"}}
      @unregisterComponent={{fn this.unregisterComponent "searchInputComponent"}}
      @value={{this.searchValue}}
      data-test-spds-input-address-autocomplete
      data-test-address-suggestions={{this.addressSuggestions.length}}
      {{this.setupComponent}}
      {{this.validateArguments}}
      ...attributes
      as |SI|
    >
      {{#if @label}}
        <SI.label />
      {{/if}}

      <SI.trigger />

      <SI.menu as |menu|>
        {{#if (isEmpty this.query)}}
          <menu.noResults @text="Start typing a street address" />
        {{else}}
          {{#if this.addressSuggestions.length}}
            {{#each this.addressSuggestions as |suggestion|}}
              <menu.item @value={{suggestion.text}}>
                {{suggestion.text}}
              </menu.item>
            {{/each}}
          {{else}}
            <menu.noResults @text="No address found" />
          {{/if}}
        {{/if}}
      </SI.menu>

      {{#if @helperText}}
        <SI.helperText />
      {{/if}}
    </SearchInput>
  </template>
}

export const INPUT_ADDRESS_AUTOCOMPLETE_METADATA = SpdsInputAddressAutocomplete.metadata;
